import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { ArrowLeft, ChevronRight, Star, CheckCircle2 } from 'lucide-react';

const SubjectPage = () => {
  const { subject, keyStage } = useParams();
  const { api } = useAuth();
  const location = useLocation();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Detect if we're in admin preview mode
  const isAdminPreview = location.pathname.startsWith('/admin/preview');
  const basePath = isAdminPreview ? '/admin/preview' : '/student';
  const dashboardPath = isAdminPreview ? '/admin' : '/student';

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await api.get(`/v2/curriculum/topics?subject=${subject}&key_stage=${keyStage}`);
        setTopics(response.data.topics);
      } catch (error) {
        console.error('Error fetching topics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTopics();
  }, [subject, keyStage, api]);

  const subjectColors = {
    maths: { bg: 'amber', icon: '🔢' },
    english: { bg: 'pink', icon: '📚' },
    science: { bg: 'emerald', icon: '🔬' },
    esol: { bg: 'pink', icon: '🗣️' },
    '11plus': { bg: 'amber', icon: '🎯' },
  };

  const colors = subjectColors[subject] || subjectColors.maths;

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <Link 
        to={dashboardPath}
        className="inline-flex items-center gap-2 text-slate-600 hover:text-purple-600 font-semibold mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      {/* Header */}
      <div className={`student-card bg-${colors.bg}-50 border-${colors.bg}-300 mb-8`}>
        <div className="flex items-center gap-4">
          <div className="text-5xl">{colors.icon}</div>
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-800 capitalize">{subject}</h1>
            <p className="text-slate-600">{topics.length} topics to explore</p>
          </div>
        </div>
      </div>

      {/* Topics Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {topics.map(topic => {
          const progress = topic.completed_lessons || 0;
          const total = topic.lessons_count || 1;
          const percentage = Math.round((progress / total) * 100);

          return (
            <Link 
              key={topic.id} 
              to={`${basePath}/topic/${topic.id}`}
              data-testid={`topic-${topic.id}`}
            >
              <div className="student-card cursor-pointer group">
                <div className="flex items-start gap-4">
                  <div 
                    className="w-14 h-14 rounded-2xl border-2 border-gray-800 flex items-center justify-center shrink-0"
                    style={{ backgroundColor: topic.color + '30' }}
                  >
                    <span className="text-2xl" style={{ color: topic.color }}>
                      {topic.icon === 'Calculator' ? '🔢' :
                       topic.icon === 'BookOpen' ? '📖' :
                       topic.icon === 'FlaskConical' ? '🧪' :
                       topic.icon === 'Brain' ? '🧠' : '📝'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-lg font-bold text-slate-800">{topic.name}</h3>
                      {percentage === 100 && <CheckCircle2 className="w-6 h-6 text-green-500" />}
                    </div>
                    <p className="text-slate-600 text-sm mt-1">{topic.description}</p>
                    
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-slate-500">{progress}/{total} lessons</span>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          <span className="font-semibold text-amber-600">{topic.total_stars || 0}</span>
                        </div>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden border border-gray-300">
                        <div 
                          className="h-full rounded-full"
                          style={{ width: `${percentage}%`, backgroundColor: topic.color }}
                        />
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </StudentLayout>
  );
};

export default SubjectPage;
